import { useState, useEffect } from 'react';
import { 
  Activity, CheckCircle, XCircle, Clock, Smartphone, Mail, 
  RefreshCw, ArrowUpRight 
} from 'lucide-react';
import { collection, query, orderBy, limit, onSnapshot } from 'firebase/firestore';
import { db } from '../../utils/firebase/client';
import { getCompanyId } from '../../utils/tenantContext';
import { format } from 'date-fns';

interface NotificationLog {
  id: string;
  type: 'alert' | 'ticket' | 'system';
  severity: 'critical' | 'high' | 'medium' | 'low';
  title: string;
  message: string;
  channels: {
    sms?: { status: 'sent'|'delivered'|'failed'; sentAt: any; error?: string };
    email?: { status: 'sent'|'delivered'|'failed'; sentAt: any };
    push?: { status: 'sent'|'delivered'|'failed'; sentAt: any };
    voice?: { status: 'sent'|'delivered'|'failed'; sentAt: any };
  };
  createdAt: any;
  recipientId: string;
  recipientName: string;
}

export default function DeliveryTrackingDashboard({ isDarkMode = false }: { isDarkMode?: boolean }) {
  const [logs, setLogs] = useState<NotificationLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all'|'failed'|'critical'>('all');
  const companyId = getCompanyId();

  useEffect(() => {
    if (!companyId) return;

    // Real-time listener for notification logs
    const q = query(
      collection(db, `companies/${companyId}/notification_logs`),
      orderBy('createdAt', 'desc'),
      limit(50)
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const data = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as NotificationLog[];
      setLogs(data);
      setLoading(false);
    }, (error) => {
      console.error("Error fetching logs:", error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [companyId]);

  const getStatusIcon = (status?: string) => {
    switch(status) {
      case 'delivered': return <CheckCircle className="w-4 h-4 text-emerald-500" />;
      case 'sent': return <Clock className="w-4 h-4 text-blue-500" />;
      case 'failed': return <XCircle className="w-4 h-4 text-red-500" />;
      default: return <div className="w-4 h-4 rounded-full border-2 border-gray-300" />;
    }
  };

  const filteredLogs = logs.filter(log => {
    if (filter === 'failed') {
      return Object.values(log.channels).some(c => c?.status === 'failed');
    }
    if (filter === 'critical') {
      return log.severity === 'critical';
    }
    return true;
  });

  return (
    <div className={`max-w-6xl mx-auto p-6 ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>
      <div className="flex justify-between items-end mb-8">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Activity className="w-6 h-6 text-blue-600" />
            Delivery Tracking
          </h1>
          <p className={`mt-1 text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            Real-time audit trail of all system notifications and delivery statuses.
          </p>
        </div>
        
        <div className={`flex p-1 rounded-xl ${isDarkMode ? 'bg-slate-800' : 'bg-gray-100'}`}>
          {(['all', 'failed', 'critical'] as const).map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`
                px-4 py-2 rounded-lg text-sm font-medium transition-all capitalize
                ${filter === f 
                  ? 'bg-white text-blue-600 shadow-sm dark:bg-slate-700 dark:text-white' 
                  : 'text-gray-500 hover:text-gray-700 dark:text-gray-400'}
              `}
            >
              {f}
            </button>
          ))}
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        {[
          { label: 'Delivery Rate', value: '98.2%', sub: 'Last 24h', color: 'text-emerald-600' },
          { label: 'Avg Latency', value: '1.2s', sub: 'P99', color: 'text-blue-600' },
          { label: 'Failed', value: logs.filter(l => Object.values(l.channels).some(c => c?.status === 'failed')).length, sub: 'Action needed', color: 'text-red-600' },
          { label: 'Total Sent', value: logs.length, sub: 'This session', color: 'text-purple-600' },
        ].map((stat, i) => (
          <div key={i} className={`p-4 rounded-2xl border ${isDarkMode ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-gray-200'}`}>
            <div className={`text-sm font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{stat.label}</div>
            <div className={`text-2xl font-bold mt-1 ${stat.color}`}>{stat.value}</div>
            <div className="text-xs text-gray-400 mt-1">{stat.sub}</div>
          </div>
        ))}
      </div>

      {/* Logs Table */}
      <div className={`rounded-2xl border overflow-hidden ${isDarkMode ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-gray-200'}`}>
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className={`border-b ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-gray-50 border-gray-100'}`}>
              <tr>
                <th className="px-6 py-4 font-semibold text-gray-500">Time</th>
                <th className="px-6 py-4 font-semibold text-gray-500">Recipient</th>
                <th className="px-6 py-4 font-semibold text-gray-500">Message</th>
                <th className="px-6 py-4 font-semibold text-gray-500">Channels</th>
                <th className="px-6 py-4 font-semibold text-gray-500">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-slate-700">
              {loading ? (
                <tr>
                  <td colSpan={5} className="px-6 py-8 text-center text-gray-500">
                    Loading logs...
                  </td>
                </tr>
              ) : filteredLogs.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-6 py-8 text-center text-gray-500">
                    No logs found matching filter.
                  </td>
                </tr>
              ) : (
                filteredLogs.map((log) => (
                  <tr key={log.id} className={`group ${isDarkMode ? 'hover:bg-slate-800' : 'hover:bg-gray-50'}`}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className={`font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-900'}`}>
                        {log.createdAt?.toDate ? format(log.createdAt.toDate(), 'HH:mm:ss') : 'Just now'}
                      </div>
                      <div className="text-xs text-gray-500">
                        {log.createdAt?.toDate ? format(log.createdAt.toDate(), 'MMM d') : ''}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className={`font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-900'}`}>{log.recipientName}</div>
                      <div className="text-xs text-gray-500">ID: {log.recipientId.slice(0, 6)}...</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`
                          px-2 py-0.5 rounded-full text-[10px] font-bold uppercase
                          ${log.severity === 'critical' ? 'bg-red-100 text-red-600' :
                            log.severity === 'high' ? 'bg-orange-100 text-orange-600' :
                            'bg-blue-100 text-blue-600'}
                        `}>
                          {log.severity}
                        </span>
                        <span className={`font-medium truncate max-w-[200px] ${isDarkMode ? 'text-gray-300' : 'text-gray-900'}`}>
                          {log.title}
                        </span>
                      </div>
                      <div className="text-xs text-gray-500 truncate max-w-[250px]">{log.message}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex gap-3">
                        {log.channels.sms && (
                          <div className="flex flex-col items-center gap-1" title={`SMS: ${log.channels.sms.status}`}>
                            <Smartphone className={`w-4 h-4 ${log.channels.sms.status === 'failed' ? 'text-red-500' : 'text-gray-400'}`} />
                            {getStatusIcon(log.channels.sms.status)}
                          </div>
                        )}
                        {log.channels.email && (
                          <div className="flex flex-col items-center gap-1" title={`Email: ${log.channels.email.status}`}>
                            <Mail className={`w-4 h-4 ${log.channels.email.status === 'failed' ? 'text-red-500' : 'text-gray-400'}`} />
                            {getStatusIcon(log.channels.email.status)}
                          </div>
                        )}
                        {log.channels.push && (
                          <div className="flex flex-col items-center gap-1" title={`Push: ${log.channels.push.status}`}>
                            <ArrowUpRight className={`w-4 h-4 ${log.channels.push.status === 'failed' ? 'text-red-500' : 'text-gray-400'}`} />
                            {getStatusIcon(log.channels.push.status)}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {Object.values(log.channels).some(c => c?.status === 'failed') ? (
                        <button className="text-xs font-medium text-red-600 hover:underline flex items-center gap-1">
                          <RefreshCw className="w-3 h-3" /> Retry
                        </button>
                      ) : (
                        <span className="text-xs font-medium text-emerald-600 flex items-center gap-1">
                          <CheckCircle className="w-3 h-3" /> Complete
                        </span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
